import React, { useState, useEffect } from "react";
import { Theme, useTheme } from '@mui/material/styles'
import { makeStyles,} from "@mui/styles";
import {
  Button,
  Chip,
  FormControl,
  Grid,
  Input,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
} from "@mui/material";
import { getSalesOfficesAndRoutes } from "../../utils/GetPlanning";
import requestMissingContexts, { determineConfig, displayConfig } from "../../utils/GetContext";
import { useLocalStorage } from "../../componentUtils/LocalStorage";

interface Inputs {
  onSelectionChange: any;
  onThresholdChange: any;
}

interface HeaderState {
  isLoaded: boolean,
  allDeliveringPlants: any,
  allRoutes: any,
  allSubTradeChannels: any,
  filteredRoutes: string[],
  deliveringPlants: string[],
  routes: string[],
  dateFrom: string,
  dateTo: string,
  moneyThreshold: string,
}

interface DeliveringPlant {
  deliveringPlant: string,
  name: string,
}

export default function VMDEXHeader(props: Inputs) {
  const [defaultSalesOffice,] = useLocalStorage("SETTINGS.defaultSalesOffice", '');

  const theme: Theme = useTheme();
  const useStyles = makeStyles((theme: Theme) => ({
    cont: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(3),
    },
    grid: {
      marginLeft: theme.spacing(1),
    },
    load: {
      textAlign: "center",
      justify: "center",
    },
    chips: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    chip: {
      margin: 2,
    },
    formControl: {
      minWidth: 200,
      margin: theme.spacing(1),
    },
  }));

  var dateFrom  = new Date();
  dateFrom.setDate(dateFrom.getDate() - 1);
  var dateTo = new Date();
  const dateFromStr = dateFrom.toISOString().split('T')[0];
  const dateToStr = dateTo.toISOString().split('T')[0];

  const classes = useStyles();
  const [state, setState] = useState<HeaderState>({
    isLoaded: false,
    allDeliveringPlants: [],
    allRoutes: [],
    allSubTradeChannels: [],
    filteredRoutes: [],
    deliveringPlants:[],
    routes: [],
    dateFrom: dateFromStr,
    dateTo: dateToStr,
    moneyThreshold: "2.00",
  });

  const [salesOrg,] = useLocalStorage("SETTINGS.salesOrg", "");
  useEffect(() => {
    if (state.isLoaded) return;

    getSalesOfficesAndRoutes(salesOrg.toString()).then(
      (v:any) => {
        const r_so:any[] = v.plants;
        const r_rt:any[] = v.routes;
        if (r_so && r_rt) {
          requestMissingContexts(["PLANTS"]).then((ctx) => {
            const contexts:any = ctx;
            var deliveringPlants: Array<DeliveringPlant> = [];
            for (var i=0; i<r_so.length; i++) {
              const so = r_so[i].plant;
              const desc = so + ": " + determineConfig(
                contexts,
                "PLANTS", 
                so || ""
              );
              const deliveringPlant:DeliveringPlant = {deliveringPlant: so, name: desc};
              deliveringPlants.push(deliveringPlant);
            }
            let rts:any[] = [] 
            if(defaultSalesOffice !== ''){
              rts = rts.concat(r_rt.filter((elem: { deliveringPlant: any; }) => elem.deliveringPlant === defaultSalesOffice)).map((rts:any)=>rts.route);
            }
            setState({
              ...state,
              isLoaded: true,
              allDeliveringPlants: deliveringPlants,
              allRoutes: r_rt,
              deliveringPlants:defaultSalesOffice?[defaultSalesOffice]:[],
              filteredRoutes: rts,
            });
          });
        }
      }
    )
  }, [state]);

  const handleSOChange = (event: any) => {
    let value = event.target.value;
    if (value) {
      // get all combined routes
      var filteredRoutes = new Map<string, boolean>();
      for(var i:number = 0; i<value.length; i++) {
        const rts = state.allRoutes.filter((elem: { deliveringPlant: any; }) => elem.deliveringPlant === value[i]);
        for(var j:number = 0; j<rts.length; j++) {
          filteredRoutes.set(rts[j].route, true);
        }
      }
      const ftr = Array.from(filteredRoutes.keys());
      setState({...state, deliveringPlants: value, filteredRoutes: ftr});
    }
  }

  const handleRouteChange = (event: any) => {
    let value = event.target.value;
    if (value) {
      setState({...state, routes: value});
    }
  }

  const handleDateFromChange = (event: any) => {
    setState({...state, dateFrom: event.target.value});
  }

  const handleDateToChange = (event: any) => {
    setState({...state, dateTo: event.target.value});
  }

  const handleMoneyChange = (event: any) => {
    setState({...state, moneyThreshold: event.target.value});
    props.onThresholdChange(event.target.value);
  }

  const getStyles = (key: string, arr: string[]) => {
    return {
      fontWeight:
        arr.indexOf(key) === -1
          ? theme.typography.fontWeightRegular
          : theme.typography.fontWeightMedium,
    };
  }


  return (
    <Paper className={classes.cont}>
      <Grid className={classes.grid} container spacing={3}>
        <Grid item lg={2} md={2} xl={2} xs={2}>
        <FormControl className={classes.formControl} fullWidth>
        <InputLabel id="select-multiple-so-label">Delivery Plant</InputLabel>
        <Select
          multiple
          fullWidth
          value={state.deliveringPlants || ""}
          onChange={handleSOChange}
          input={<Input id="select-multiple-so" />}
          renderValue={(selected: any) => (
            <div className={classes.chips}>
              {selected.map((value: any) => (
                <Chip key={value} label={value} className={classes.chip} />
              ))}
            </div>
          )}
        >
          {state.allDeliveringPlants.map((option: any) => (
            <MenuItem key={option.deliveringPlant} value={option.deliveringPlant} style={getStyles(option.deliveringPlant, state.allDeliveringPlants)}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
        </FormControl>
        </Grid>

        <Grid item lg={2} md={2} xl={2} xs={2}>
        <FormControl className={classes.formControl} fullWidth>
        <InputLabel id="select-multiple-route-label">Routes</InputLabel>
        <Select
          multiple
          fullWidth
          value={state.routes || ""}
          onChange={handleRouteChange}
          input={<Input id="select-multiple-route" />}
          renderValue={(selected: any) => (
            <div className={classes.chips}>
              {selected.map((value: any) => (
                <Chip key={value} label={value} className={classes.chip} />
              ))}
            </div>
          )}
        >
          {state.filteredRoutes.map((route, index: number) => (
            <MenuItem key={index} value={route} style={getStyles(route, state.filteredRoutes)}>
              {route}
            </MenuItem>
          ))}
        </Select>
        </FormControl>
        </Grid>

        <Grid item lg={2} md={2} xl={2} xs={2}>
          <TextField label="Date From" fullWidth={true} type="date" value={state.dateFrom} onChange={(ce)=>handleDateFromChange(ce)}/>
        </Grid>
        <Grid item lg={2} md={2} xl={2} xs={2}>
          <TextField label="Date To" fullWidth={true} type="date" value={state.dateTo} onChange={(ce)=>handleDateToChange(ce)}/>
        </Grid>
        <Grid item lg={2} md={2} xl={2} xs={2}>
          <TextField label="Money Threshold" fullWidth={true} type="text" value={state.moneyThreshold} onChange={(ce)=>handleMoneyChange(ce)}/>
        </Grid>
        <Grid item className={classes.load} lg={2} md={2} xl={2} xs={2}>
          <Button onClick={() => props.onSelectionChange(state.deliveringPlants, state.routes, state.dateFrom, state.dateTo, state.moneyThreshold)}>Load VM-DEX Data</Button>
        </Grid>
      </Grid>
    </Paper>
  );
}
